import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import type { V10Palette } from '../v10/theme';
import { GlassCard, Atmos } from '../v10/ui';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import type { ToolProps } from '../olmps/ToolHost';

// ---------------------------------------------------------------------------
// Types — mirrored from src/interfaces/moyri.ts (web golden reference)
// ---------------------------------------------------------------------------
type HabitCategory = 'cultivate' | 'maintain' | 'degrade';
type HabitStatus = 'active' | 'resolved';

interface SpectrumFields {
  negative: string;
  neutral: string;
  positive: string;
}

interface DeityHalf {
  name: string;
  icon: string;
}

interface DeityPair {
  negative: DeityHalf;
  positive: DeityHalf;
}

interface MoyriHabit {
  id: string;
  user_id: string;
  category: HabitCategory;
  name: string;
  function_tag?: string | null;
  summary?: string | null;
  image_url?: string | null;
  status: HabitStatus;
  symptoms: string[];
  absence_signs: string[];
  triggers: string[];
  dissolve_actions: string[];
  prevention_actions: string[];
  spectrum?: SpectrumFields | null;
  deity_pair?: DeityPair | null;
  what_it_is?: string | null;
  survival_basis?: string | null;
  how_you_got_here?: string | null;
  what_makes_it_worse?: string | null;
  what_it_costs?: string | null;
  related_conditions?: string | null;
  success_count: number;
  failure_count: number;
  check_in_reminder?: string | null;
  spectrum_rating?: number | null;
  linked_habit_ids: string[];
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

interface MoyriLog {
  id: string;
  user_id: string;
  habit_id: string;
  date: string;
  success: boolean;
  note?: string | null;
  created_at?: string;
}

interface MoyriChatMessage {
  id: string;
  user_id: string;
  habit_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
}

interface MoyriAiResponse {
  habitId: string;
  message: string;
  newHabitId?: string;
}

type CategoryMeta = { label: string; sublabel: string; color: string; bgColor: string; borderColor: string; tile: string };
function PageBg() {
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <Atmos />
    </View>
  );
}

function makeCategoryMeta(t: V10Palette): Record<HabitCategory, CategoryMeta> {
  return {
    cultivate: { label: 'CLO', sublabel: 'Cultivate', color: t.onPace, bgColor: t.glass, borderColor: t.navyLine, tile: t.onPace },
    maintain: { label: 'LAX', sublabel: 'Maintain', color: t.behind, bgColor: t.glass, borderColor: t.navyLine, tile: t.behind },
    degrade: { label: 'TRO', sublabel: 'Degrade', color: t.lapsed, bgColor: t.glass, borderColor: t.navyLine, tile: t.lapsed },
  };
}

const CATEGORY_ORDER: HabitCategory[] = ['cultivate', 'maintain', 'degrade'];

// State + accent colours resolved from the live v10 palette.
function makePalette(t: V10Palette) {
  return {
    green500: t.onPace,
    green600: t.onPace,
    greenBg: t.glass,
    greenBorder: t.navyLine,
    amber500: t.behind,
    amber600: t.behind,
    red500: t.lapsed,
    red600: t.lapsed,
    redBg: t.glass,
    redBorder: t.navyLine,
    blue500: t.gold,
    blue600: t.goldDeep,
    purple500: t.goldBright,
    purple600: t.goldBright,
    grayBg: t.navyRaised,
    grayBorder: t.navyLine,
    muted: t.ivorySoft,
  };
}

type ArrayField = { key: keyof Pick<
  MoyriHabit,
  'symptoms' | 'absence_signs' | 'triggers' | 'dissolve_actions' | 'prevention_actions'
>; label: string; dotColor: string; headerLabel: string };
function makeArrayFields(t: V10Palette): ArrayField[] {
  const palette = makePalette(t);
  return [
    { key: 'symptoms', label: 'Symptoms (Step 1)', dotColor: palette.amber500, headerLabel: '1. Is it here? (Symptoms)' },
    { key: 'absence_signs', label: 'Absence Signs (Step 2)', dotColor: palette.green500, headerLabel: '2. Is it gone? (Absence Signs)' },
    { key: 'triggers', label: 'Triggers (Step 3)', dotColor: palette.red500, headerLabel: '3. What triggered it?' },
    { key: 'dissolve_actions', label: 'Dissolve Actions (Step 4)', dotColor: palette.blue500, headerLabel: '4. Dissolve it now' },
    { key: 'prevention_actions', label: 'Prevention Actions (Step 5)', dotColor: palette.purple500, headerLabel: '5. Keep it gone' },
  ];
}

const TEXT_FIELDS: { key: keyof Pick<
  MoyriHabit,
  'what_it_is' | 'survival_basis' | 'how_you_got_here' | 'what_makes_it_worse' | 'what_it_costs' | 'related_conditions'
>; label: string }[] = [
  { key: 'what_it_is', label: 'What It Is' },
  { key: 'survival_basis', label: 'Survival Basis' },
  { key: 'how_you_got_here', label: 'How You Got Here' },
  { key: 'what_makes_it_worse', label: 'What Makes It Worse' },
  { key: 'what_it_costs', label: 'What It Costs You' },
  { key: 'related_conditions', label: 'Related Conditions' },
];

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function tileLetter(name: string): string {
  return (name || '?').charAt(0).toUpperCase();
}

function blankHabit(userId: string, category: HabitCategory): Omit<MoyriHabit, 'id'> {
  return {
    user_id: userId,
    category,
    name: 'New Habit',
    function_tag: '',
    summary: '',
    image_url: '',
    status: 'active',
    symptoms: [],
    absence_signs: [],
    triggers: [],
    dissolve_actions: [],
    prevention_actions: [],
    spectrum: { negative: '', neutral: '', positive: '' },
    deity_pair: { negative: { name: '', icon: '' }, positive: { name: '', icon: '' } },
    what_it_is: '',
    survival_basis: '',
    how_you_got_here: '',
    what_makes_it_worse: '',
    what_it_costs: '',
    related_conditions: '',
    success_count: 0,
    failure_count: 0,
    check_in_reminder: '',
    spectrum_rating: 5,
    linked_habit_ids: [],
    sort_order: 0,
  };
}

// ---------------------------------------------------------------------------
// Small reusable widgets
// ---------------------------------------------------------------------------
function RatingRow({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  const styles = useStyles();
  const options = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <View style={styles.ratingRow}>
      {options.map((n) => {
        const active = n === value;
        return (
          <Pressable
            key={n}
            style={[styles.ratingDot, active && styles.ratingDotActive]}
            onPress={() => onChange(n)}
          >
            <Text style={[styles.ratingDotText, active && styles.ratingDotTextActive]}>{n}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function HabitTile({ habit, size }: { habit: MoyriHabit; size: number }) {
  const { t } = useTheme();
  const palette = makePalette(t);
  const CATEGORY_META = makeCategoryMeta(t);
  const styles = useStyles();
  const meta = CATEGORY_META[habit.category];
  if (habit.image_url) {
    return (
      <Image
        source={{ uri: habit.image_url }}
        style={{ width: size, height: size, borderRadius: radii.tile, backgroundColor: palette.grayBg }}
      />
    );
  }
  return (
    <View
      style={[
        styles.tileFallback,
        { width: size, height: size, backgroundColor: meta.tile, borderRadius: radii.tile },
      ]}
    >
      <Text style={[styles.tileFallbackText, { fontSize: size * 0.4 }]}>{tileLetter(habit.name)}</Text>
    </View>
  );
}

function BulletList({ items, dotColor }: { items: string[]; dotColor: string }) {
  const styles = useStyles();
  return (
    <View style={{ gap: 4 }}>
      {items.map((s, i) => (
        <View key={i} style={styles.bulletRow}>
          <Text style={[styles.bulletDash, { color: dotColor }]}>--</Text>
          <Text style={styles.bulletText}>{s}</Text>
        </View>
      ))}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function MoyriTool(_props: ToolProps) {
  const styles = useStyles();
  const { t, c } = useTheme();
  const palette = makePalette(t);
  const CATEGORY_META = makeCategoryMeta(t);
  const ARRAY_FIELDS = makeArrayFields(t);
  const { session } = useAuth();
  const userId = session?.user.id;

  const [habits, setHabits] = useState<MoyriHabit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [screen, setScreen] = useState<'home' | 'category'>('home');
  const [activeCategory, setActiveCategory] = useState<HabitCategory | null>(null);

  const [detailHabitId, setDetailHabitId] = useState<string | null>(null);
  const [logs, setLogs] = useState<MoyriLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<MoyriHabit>>({});
  const [busy, setBusy] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // Per-habit coaching chat
  const [chatHabitId, setChatHabitId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<MoyriChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatScrollRef = useRef<ScrollView>(null);

  // AI Habit Builder chat
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderCategory, setBuilderCategory] = useState<HabitCategory | null>(null);
  const [builderMessages, setBuilderMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [builderInput, setBuilderInput] = useState('');
  const [builderSending, setBuilderSending] = useState(false);
  const [builderNewHabitId, setBuilderNewHabitId] = useState<string | null>(null);
  const builderScrollRef = useRef<ScrollView>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data, error: err } = await supabase
      .from('moyri_habits')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('sort_order', { ascending: true });
    if (err) {
      setError(err.message);
    } else {
      setError(null);
      setHabits((data ?? []) as MoyriHabit[]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const habitsByCategory = useCallback(
    (cat: HabitCategory) => habits.filter((h) => h.category === cat),
    [habits]
  );

  const detailHabit = useMemo(
    () => habits.find((h) => h.id === detailHabitId) ?? null,
    [habits, detailHabitId]
  );

  // -------------------------------------------------------------------------
  // CRUD
  // -------------------------------------------------------------------------
  const createHabit = async (data: Omit<MoyriHabit, 'id'>): Promise<MoyriHabit | null> => {
    const { data: inserted, error: err } = await supabase
      .from('moyri_habits')
      .insert(data)
      .select()
      .single();
    if (err || !inserted) {
      setError(err?.message ?? 'Failed to create habit');
      return null;
    }
    const habit = inserted as MoyriHabit;
    setHabits((prev) => [...prev, habit]);
    return habit;
  };

  const updateHabit = async (id: string, data: Partial<MoyriHabit>) => {
    if (!userId) return;
    const { error: err } = await supabase
      .from('moyri_habits')
      .update(data)
      .eq('id', id)
      .eq('user_id', userId);
    if (err) {
      setError(err.message);
      return;
    }
    setHabits((prev) => prev.map((h) => (h.id === id ? { ...h, ...data } : h)));
  };

  const deleteHabit = async (id: string) => {
    if (!userId) return;
    setHabits((prev) => prev.filter((h) => h.id !== id));
    const { error: err } = await supabase
      .from('moyri_habits')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);
    if (err) {
      setError(err.message);
      await load();
      return;
    }
    setDetailHabitId(null);
    setSettingsOpen(false);
  };

  const confirmDelete = (habit: MoyriHabit) => {
    Alert.alert('Delete Habit?', 'This will permanently delete this habit and all its data. This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => void deleteHabit(habit.id) },
    ]);
  };

  const logEntry = async (habitId: string, success: boolean) => {
    if (!userId) return;
    const { error: err } = await supabase.from('moyri_logs').insert({
      user_id: userId,
      habit_id: habitId,
      date: todayStr(),
      success,
      note: null,
    });
    if (err) {
      setError(err.message);
      return;
    }
    const habit = habits.find((h) => h.id === habitId);
    if (habit) {
      const field = success ? 'success_count' : 'failure_count';
      const newCount = (habit[field] || 0) + 1;
      await supabase
        .from('moyri_habits')
        .update({ [field]: newCount })
        .eq('id', habitId)
        .eq('user_id', userId);
      setHabits((prev) => prev.map((h) => (h.id === habitId ? { ...h, [field]: newCount } : h)));
    }
    if (detailHabitId === habitId) {
      void fetchLogs(habitId);
    }
  };

  const fetchLogs = async (habitId: string) => {
    if (!userId) return;
    setLogsLoading(true);
    const { data, error: err } = await supabase
      .from('moyri_logs')
      .select('*')
      .eq('habit_id', habitId)
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(30);
    if (err) {
      setError(err.message);
    }
    setLogs((data ?? []) as MoyriLog[]);
    setLogsLoading(false);
  };

  const updateSpectrumRating = async (habitId: string, rating: number) => {
    if (!userId) return;
    await supabase
      .from('moyri_habits')
      .update({ spectrum_rating: rating })
      .eq('id', habitId)
      .eq('user_id', userId);
    setHabits((prev) => prev.map((h) => (h.id === habitId ? { ...h, spectrum_rating: rating } : h)));
  };

  // -------------------------------------------------------------------------
  // Image upload (mirrors web's supabase.storage.from('moyri-images') path)
  // -------------------------------------------------------------------------
  const pickAndUploadImage = async () => {
    if (!userId || imageUploading) return;
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
      Alert.alert('Permission needed', 'Allow photo library access to upload a habit image.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.8,
      allowsEditing: true,
      aspect: [1, 1],
    });
    if (result.canceled || !result.assets?.[0]) return;

    const asset = result.assets[0];
    setImageUploading(true);
    try {
      const ext = (asset.fileName?.split('.').pop() || asset.uri.split('.').pop() || 'jpg').toLowerCase();
      const storagePath = `${userId}/${Date.now()}.${ext}`;
      const res = await fetch(asset.uri);
      const arrayBuffer = await res.arrayBuffer();
      const contentType = asset.mimeType || `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      const { error: uploadErr } = await supabase.storage
        .from('moyri-images')
        .upload(storagePath, arrayBuffer, { contentType });
      if (uploadErr) {
        setError(uploadErr.message);
        return;
      }
      const { data: urlData } = supabase.storage.from('moyri-images').getPublicUrl(storagePath);
      updateEditField('image_url', urlData.publicUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Image upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Per-habit coaching chat (mirrors web moyri page ~574-640; moyri_chat_messages)
  // -------------------------------------------------------------------------
  const loadChatMessages = async (habitId: string) => {
    setChatLoading(true);
    const { data, error: err } = await supabase
      .from('moyri_chat_messages')
      .select('*')
      .eq('habit_id', habitId)
      .order('created_at', { ascending: true });
    if (err) setError(err.message);
    setChatMessages((data ?? []) as MoyriChatMessage[]);
    setChatLoading(false);
  };

  const openChat = (habitId: string) => {
    setChatHabitId(habitId);
    setChatMessages([]);
    setChatInput('');
    void loadChatMessages(habitId);
  };

  const closeChat = () => {
    setChatHabitId(null);
    setChatMessages([]);
    setChatInput('');
  };

  const sendChatMessage = async () => {
    const trimmed = chatInput.trim();
    if (!trimmed || chatSending || !userId || !chatHabitId) return;

    setChatInput('');
    const tempMsg: MoyriChatMessage = {
      id: `temp-${Date.now()}`,
      user_id: userId,
      habit_id: chatHabitId,
      role: 'user',
      content: trimmed,
      created_at: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, tempMsg]);
    setChatSending(true);
    try {
      const data = await apiFetch<MoyriAiResponse>('/api/ai/moyri', {
        method: 'POST',
        body: JSON.stringify({ mode: 'chat', habitId: chatHabitId, message: trimmed }),
      });
      setChatMessages((prev) => [
        ...prev,
        {
          id: `resp-${Date.now()}`,
          user_id: userId,
          habit_id: chatHabitId,
          role: 'assistant',
          content: data.message,
          created_at: new Date().toISOString(),
        },
      ]);
    } catch (e) {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          user_id: userId,
          habit_id: chatHabitId,
          role: 'assistant',
          content: `Error: ${e instanceof Error ? e.message : 'Chat failed'}`,
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setChatSending(false);
      requestAnimationFrame(() => chatScrollRef.current?.scrollToEnd({ animated: true }));
    }
  };

  // -------------------------------------------------------------------------
  // AI Habit Builder chat (mirrors web moyri page ~642-684)
  // -------------------------------------------------------------------------
  const openBuilder = (cat: HabitCategory) => {
    setBuilderCategory(cat);
    setBuilderMessages([
      {
        role: 'assistant',
        content:
          "Welcome to the MoyRi Habit Builder. Describe the habit, pattern, or emotional state you want to track, and I'll help you build a complete profile with all the Buddha Steps filled in.",
      },
    ]);
    setBuilderInput('');
    setBuilderNewHabitId(null);
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setBuilderCategory(null);
    setBuilderMessages([]);
    setBuilderInput('');
    setBuilderNewHabitId(null);
  };

  const sendBuilderMessage = async () => {
    const trimmed = builderInput.trim();
    if (!trimmed || builderSending || !userId) return;

    setBuilderInput('');
    setBuilderMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setBuilderSending(true);
    try {
      const data = await apiFetch<MoyriAiResponse>('/api/ai/moyri', {
        method: 'POST',
        body: JSON.stringify({ mode: 'builder', message: trimmed, category: builderCategory }),
      });
      setBuilderMessages((prev) => [...prev, { role: 'assistant', content: data.message }]);
      if (data.newHabitId) {
        setBuilderNewHabitId(data.newHabitId);
        await load();
      }
    } catch (e) {
      setBuilderMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${e instanceof Error ? e.message : 'Builder failed'}` },
      ]);
    } finally {
      setBuilderSending(false);
      requestAnimationFrame(() => builderScrollRef.current?.scrollToEnd({ animated: true }));
    }
  };

  const viewBuiltHabit = () => {
    if (!builderNewHabitId) return;
    const id = builderNewHabitId;
    closeBuilder();
    openDetail(id);
  };

  // -------------------------------------------------------------------------
  // Navigation helpers
  // -------------------------------------------------------------------------
  const goHome = () => {
    setScreen('home');
    setActiveCategory(null);
  };

  const goCategory = (cat: HabitCategory) => {
    setActiveCategory(cat);
    setScreen('category');
  };

  const openDetail = (habitId: string) => {
    setDetailHabitId(habitId);
    setLogs([]);
    void fetchLogs(habitId);
  };

  const closeDetail = () => {
    setDetailHabitId(null);
    setLogs([]);
  };

  const openSettingsForHabit = (habit: MoyriHabit) => {
    setEditForm({ ...habit });
    setSettingsOpen(true);
  };

  const openSettingsForNewHabit = async (cat: HabitCategory) => {
    if (!userId || busy) return;
    setBusy(true);
    const created = await createHabit(blankHabit(userId, cat) as Omit<MoyriHabit, 'id'>);
    setBusy(false);
    if (created) {
      setEditForm({ ...created });
      setSettingsOpen(true);
    }
  };

  const closeSettings = () => {
    setSettingsOpen(false);
    setEditForm({});
  };

  // -------------------------------------------------------------------------
  // Edit form helpers
  // -------------------------------------------------------------------------
  const updateEditField = (field: keyof MoyriHabit, value: unknown) => {
    setEditForm((prev) => ({ ...prev, [field]: value as never }));
  };

  const updateEditArrayItem = (field: string, index: number, value: string) => {
    const arr = [...(((editForm as unknown) as Record<string, string[]>)[field] ?? [])];
    arr[index] = value;
    setEditForm((prev) => ({ ...prev, [field]: arr }));
  };

  const addEditArrayItem = (field: string) => {
    const arr = [...(((editForm as unknown) as Record<string, string[]>)[field] ?? []), ''];
    setEditForm((prev) => ({ ...prev, [field]: arr }));
  };

  const removeEditArrayItem = (field: string, index: number) => {
    const arr = [...(((editForm as unknown) as Record<string, string[]>)[field] ?? [])];
    arr.splice(index, 1);
    setEditForm((prev) => ({ ...prev, [field]: arr }));
  };

  const saveSettings = async () => {
    if (!editForm.id) return;
    const { id, user_id, created_at, updated_at, ...data } = editForm as MoyriHabit;
    await updateHabit(id, data);
    setSettingsOpen(false);
    setEditForm({});
  };

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={c.blueText} />
      </View>
    );
  }

  return (
    <View style={styles.root}>
<PageBg />
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {screen === 'home' ? (
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.header}>
            <Text style={styles.title}>MoyRi</Text>
            <Text style={styles.subtitle}>Habit Tracking System</Text>
          </View>

          {CATEGORY_ORDER.map((cat) => {
            const meta = CATEGORY_META[cat];
            const catHabits = habitsByCategory(cat);
            return (
              <View key={cat} style={styles.categorySection}>
                <Pressable
                  style={[
                    styles.categoryHeaderCard,
                    { backgroundColor: meta.bgColor, borderColor: meta.borderColor },
                  ]}
                  onPress={() => goCategory(cat)}
                >
                  <Text style={[styles.categoryHeaderTitle, { color: t.goldBright }]}>
                    {meta.label}: {meta.sublabel}
                  </Text>
                  <Text style={styles.categoryHeaderCount}>
                    {catHabits.length} habit{catHabits.length !== 1 ? 's' : ''}
                  </Text>
                </Pressable>

                {catHabits.length === 0 ? (
                  <Text style={styles.empty}>No habits in this category yet.</Text>
                ) : (
                  <View style={styles.previewGrid}>
                    {catHabits.map((habit) => (
                      <Pressable
                        key={habit.id}
                        style={styles.previewCard}
                        onPress={() => openDetail(habit.id)}
                      >
                        <HabitTile habit={habit} size={72} />
                        <Text style={styles.previewName} numberOfLines={1}>
                          {habit.name}
                        </Text>
                        <View style={styles.countRow}>
                          <Text style={[styles.countText, { color: palette.green600 }]}>
                            ▲ {habit.success_count || 0}
                          </Text>
                          <Text style={[styles.countText, { color: palette.red600 }]}>
                            ▼ {habit.failure_count || 0}
                          </Text>
                        </View>
                      </Pressable>
                    ))}
                  </View>
                )}

                <View style={styles.addButtonRow}>
                  <Pressable
                    style={[styles.addButton, { flex: 1 }]}
                    onPress={() => void openSettingsForNewHabit(cat)}
                    disabled={busy}
                  >
                    <Text style={styles.addButtonText}>+ Add {meta.sublabel} Habit</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.addButton, styles.addButtonAccent, { flex: 1 }]}
                    onPress={() => openBuilder(cat)}
                    disabled={busy}
                  >
                    <Text style={styles.addButtonText}>AI-Guided Builder</Text>
                  </Pressable>
                </View>
              </View>
            );
          })}
        </ScrollView>
      ) : (
        activeCategory && (
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <View style={styles.categoryScreenHeader}>
              <Pressable style={styles.backButton} onPress={goHome}>
                <Text style={styles.backButtonText}>‹ Back</Text>
              </Pressable>
              <Text style={[styles.categoryScreenTitle, { color: t.goldBright }]}>
                {CATEGORY_META[activeCategory].label}: {CATEGORY_META[activeCategory].sublabel}
              </Text>
              <Text style={styles.categoryHeaderCount}>
                {habitsByCategory(activeCategory).length} habit
                {habitsByCategory(activeCategory).length !== 1 ? 's' : ''}
              </Text>
            </View>

            {habitsByCategory(activeCategory).length === 0 ? (
              <Text style={styles.empty}>No habits in this category yet.</Text>
            ) : (
              <View style={{ gap: 10 }}>
                {habitsByCategory(activeCategory).map((habit) => (
                  <Pressable key={habit.id} style={styles.listCard} onPress={() => openDetail(habit.id)}>
                    <HabitTile habit={habit} size={64} />
                    <View style={styles.listCardBody}>
                      <View style={styles.listCardTitleRow}>
                        <Text style={styles.listCardName} numberOfLines={1}>
                          {habit.name}
                        </Text>
                        {habit.function_tag ? (
                          <GlassCard style={styles.badge}>
                            <Text style={styles.badgeText}>{habit.function_tag}</Text>
                          </GlassCard>
                        ) : null}
                      </View>
                      {habit.summary ? (
                        <Text style={styles.listCardSummary} numberOfLines={2}>
                          {habit.summary}
                        </Text>
                      ) : null}
                      <View style={styles.countRow}>
                        <Text style={[styles.countText, { color: palette.green600 }]}>
                          ▲ {habit.success_count || 0} successes
                        </Text>
                        <Text style={[styles.countText, { color: palette.red600 }]}>
                          ▼ {habit.failure_count || 0} failures
                        </Text>
                      </View>
                    </View>
                  </Pressable>
                ))}
              </View>
            )}

            <View style={styles.addButtonRow}>
              <Pressable
                style={[styles.addButton, { flex: 1 }]}
                onPress={() => void openSettingsForNewHabit(activeCategory)}
                disabled={busy}
              >
                <Text style={styles.addButtonText}>+ Add New Habit</Text>
              </Pressable>
              <Pressable
                style={[styles.addButton, styles.addButtonAccent, { flex: 1 }]}
                onPress={() => openBuilder(activeCategory)}
                disabled={busy}
              >
                <Text style={styles.addButtonText}>AI-Guided Builder</Text>
              </Pressable>
            </View>
          </ScrollView>
        )
      )}

      {/* -------------------------------------------------------------- */}
      {/* Habit detail modal (quick card + full profile, read-only)      */}
      {/* -------------------------------------------------------------- */}
      <Modal
        visible={Boolean(detailHabit)}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={closeDetail}
      >
        {detailHabit ? (
          <View style={styles.modalRoot}>
<PageBg />
            <View style={styles.modalHeaderBar}>
              <Pressable style={styles.backButton} onPress={closeDetail}>
                <Text style={styles.backButtonText}>Close</Text>
              </Pressable>
              <Text style={styles.modalHeaderTitle} numberOfLines={1}>
                {detailHabit.name}
              </Text>
              <View style={{ width: 56 }} />
            </View>
            <ScrollView contentContainerStyle={styles.scrollContent}>
              <View style={styles.heroWrap}>
                <HabitTile habit={detailHabit} size={112} />
                <Text style={styles.heroName}>{detailHabit.name}</Text>
                <View style={styles.heroBadgeRow}>
                  {detailHabit.function_tag ? (
                    <GlassCard style={styles.badge}>
                      <Text style={styles.badgeText}>{detailHabit.function_tag}</Text>
                    </GlassCard>
                  ) : null}
                  <View
                    style={[
                      styles.badge,
                      {
                        backgroundColor: CATEGORY_META[detailHabit.category].bgColor,
                        borderColor: CATEGORY_META[detailHabit.category].borderColor,
                      },
                    ]}
                  >
                    <Text style={[styles.badgeText, { color: CATEGORY_META[detailHabit.category].color }]}>
                      {CATEGORY_META[detailHabit.category].sublabel}
                    </Text>
                  </View>
                </View>
                {detailHabit.summary ? <Text style={styles.summaryText}>{detailHabit.summary}</Text> : null}
              </View>

              {ARRAY_FIELDS.map(({ key, dotColor, headerLabel }) => {
                const items = detailHabit[key] ?? [];
                if (!items || items.length === 0) return null;
                return (
                  <View key={key} style={styles.card}>
                    <Text style={[styles.cardHeader, { color: dotColor }]}>{headerLabel}</Text>
                    <BulletList items={items} dotColor={dotColor} />
                  </View>
                );
              })}

              {detailHabit.spectrum &&
              (detailHabit.spectrum.negative || detailHabit.spectrum.neutral || detailHabit.spectrum.positive) ? (
                <GlassCard style={styles.spectrumRow}>
                  <Text style={[styles.spectrumText, { color: palette.red500 }]} numberOfLines={2}>
                    {detailHabit.spectrum.negative || 'Negative'}
                  </Text>
                  <Text style={styles.spectrumTextMuted} numberOfLines={2}>
                    {detailHabit.spectrum.neutral || 'Neutral'}
                  </Text>
                  <Text style={[styles.spectrumText, { color: palette.green500 }]} numberOfLines={2}>
                    {detailHabit.spectrum.positive || 'Positive'}
                  </Text>
                </GlassCard>
              ) : null}

              {detailHabit.deity_pair &&
              (detailHabit.deity_pair.negative?.name || detailHabit.deity_pair.positive?.name) ? (
                <GlassCard style={styles.spectrumRow}>
                  <Text style={[styles.spectrumText, { color: palette.red500 }]}>
                    {detailHabit.deity_pair.negative.icon} {detailHabit.deity_pair.negative.name}
                  </Text>
                  <Text style={styles.spectrumTextMuted}>--</Text>
                  <Text style={[styles.spectrumText, { color: palette.green500 }]}>
                    {detailHabit.deity_pair.positive.icon} {detailHabit.deity_pair.positive.name}
                  </Text>
                </GlassCard>
              ) : null}

              <View style={styles.logButtonRow}>
                <Pressable
                  style={[styles.logButton, { backgroundColor: palette.green600 }]}
                  onPress={() => void logEntry(detailHabit.id, true)}
                >
                  <Text style={styles.logButtonText}>✓ Log Success</Text>
                </Pressable>
                <Pressable
                  style={[styles.logButton, { backgroundColor: palette.red600 }]}
                  onPress={() => void logEntry(detailHabit.id, false)}
                >
                  <Text style={styles.logButtonText}>✕ Log Failure</Text>
                </Pressable>
              </View>

              <View style={styles.statsRow}>
                <GlassCard style={styles.statBox}>
                  <Text style={[styles.statNumber, { color: palette.green600 }]}>
                    {detailHabit.success_count || 0}
                  </Text>
                  <Text style={styles.statLabel}>Successes</Text>
                </GlassCard>
                <GlassCard style={styles.statBox}>
                  <Text style={[styles.statNumber, { color: palette.red600 }]}>
                    {detailHabit.failure_count || 0}
                  </Text>
                  <Text style={styles.statLabel}>Failures</Text>
                </GlassCard>
              </View>

              {TEXT_FIELDS.map(({ key, label }) => {
                const value = detailHabit[key];
                if (!value) return null;
                return (
                  <View key={key} style={styles.section}>
                    <Text style={styles.sectionHeader}>{label}</Text>
                    <Text style={styles.sectionBody}>{value}</Text>
                  </View>
                );
              })}

              <View style={styles.section}>
                <Text style={styles.sectionHeader}>
                  Spectrum Rating: {detailHabit.spectrum_rating ?? 5}/10
                </Text>
                <RatingRow
                  value={detailHabit.spectrum_rating ?? 5}
                  onChange={(n) => void updateSpectrumRating(detailHabit.id, n)}
                />
                <View style={styles.ratingLabelsRow}>
                  <Text style={styles.ratingLabelText}>Negative</Text>
                  <Text style={styles.ratingLabelText}>Positive</Text>
                </View>
              </View>

              {detailHabit.linked_habit_ids && detailHabit.linked_habit_ids.length > 0 ? (
                <View style={styles.section}>
                  <Text style={styles.sectionHeader}>Linked Habits</Text>
                  <View style={styles.linkedRow}>
                    {detailHabit.linked_habit_ids.map((linkedId) => {
                      const linked = habits.find((h) => h.id === linkedId);
                      if (!linked) return null;
                      return (
                        <Pressable
                          key={linkedId}
                          style={styles.linkedBadge}
                          onPress={() => openDetail(linkedId)}
                        >
                          <Text style={styles.linkedBadgeText}>{linked.name}</Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              ) : null}

              <View style={styles.section}>
                <Text style={styles.sectionHeader}>Recent Log History</Text>
                {logsLoading ? (
                  <ActivityIndicator color={c.blueText} />
                ) : logs.length === 0 ? (
                  <Text style={styles.empty}>No logs yet.</Text>
                ) : (
                  <View style={{ gap: 4 }}>
                    {logs.map((log) => (
                      <View key={log.id} style={styles.logRow}>
                        <Text style={{ color: log.success ? palette.green600 : palette.red600 }}>
                          {log.success ? '✓' : '✕'}
                        </Text>
                        <Text style={styles.logDate}>{log.date}</Text>
                        {log.note ? <Text style={styles.logNote}>- {log.note}</Text> : null}
                      </View>
                    ))}
                  </View>
                )}
              </View>

              <View style={styles.footerButtonRow}>
                <Pressable
                  style={styles.outlineButton}
                  onPress={() => openChat(detailHabit.id)}
                >
                  <Text style={styles.outlineButtonText}>Chat</Text>
                </Pressable>
                <Pressable
                  style={styles.outlineButton}
                  onPress={() => openSettingsForHabit(detailHabit)}
                >
                  <Text style={styles.outlineButtonText}>Edit</Text>
                </Pressable>
                <Pressable
                  style={[styles.outlineButton, styles.dangerOutlineButton]}
                  onPress={() => confirmDelete(detailHabit)}
                >
                  <Text style={[styles.outlineButtonText, styles.dangerText]}>Delete</Text>
                </Pressable>
              </View>
            </ScrollView>
          </View>
        ) : null}
      </Modal>

      {/* -------------------------------------------------------------- */}
      {/* Settings / edit modal                                         */}
      {/* -------------------------------------------------------------- */}
      <Modal
        visible={settingsOpen}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={closeSettings}
      >
        <View style={styles.modalRoot}>
<PageBg />
          <View style={styles.modalHeaderBar}>
            <Pressable style={styles.backButton} onPress={closeSettings}>
              <Text style={styles.backButtonText}>Cancel</Text>
            </Pressable>
            <Text style={styles.modalHeaderTitle}>Edit Habit</Text>
            <Pressable style={styles.backButton} onPress={() => void saveSettings()}>
              <Text style={[styles.backButtonText, { fontWeight: '900' }]}>Save</Text>
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Name</Text>
              <TextInput
                style={styles.textInput}
                value={editForm.name ?? ''}
                onChangeText={(v) => updateEditField('name', v)}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Function Tag</Text>
              <TextInput
                style={styles.textInput}
                value={editForm.function_tag ?? ''}
                onChangeText={(v) => updateEditField('function_tag', v)}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Summary</Text>
              <TextInput
                style={styles.textInput}
                value={editForm.summary ?? ''}
                onChangeText={(v) => updateEditField('summary', v)}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Image</Text>
              {editForm.image_url ? (
                <View style={{ gap: 8 }}>
                  <Image
                    source={{ uri: editForm.image_url }}
                    style={{ width: 96, height: 96, borderRadius: radii.tile, backgroundColor: palette.grayBg }}
                  />
                  <View style={{ flexDirection: 'row', gap: 14 }}>
                    <Pressable onPress={() => void pickAndUploadImage()} disabled={imageUploading}>
                      {imageUploading ? (
                        <ActivityIndicator size="small" color={c.blueText} />
                      ) : (
                        <Text style={styles.linkText}>Replace image</Text>
                      )}
                    </Pressable>
                    <Pressable onPress={() => updateEditField('image_url', '')} disabled={imageUploading}>
                      <Text style={[styles.linkText, styles.dangerText]}>Remove image</Text>
                    </Pressable>
                  </View>
                </View>
              ) : (
                <Pressable onPress={() => void pickAndUploadImage()} disabled={imageUploading}>
                  {imageUploading ? (
                    <ActivityIndicator size="small" color={c.blueText} />
                  ) : (
                    <Text style={styles.linkText}>+ Upload image</Text>
                  )}
                </Pressable>
              )}
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Category</Text>
              <View style={styles.chipRow}>
                {CATEGORY_ORDER.map((cat) => {
                  const meta = CATEGORY_META[cat];
                  const active = editForm.category === cat;
                  return (
                    <Pressable
                      key={cat}
                      style={[
                        styles.chip,
                        { borderColor: meta.borderColor },
                        active && { backgroundColor: meta.tile, borderColor: meta.tile },
                      ]}
                      onPress={() => updateEditField('category', cat)}
                    >
                      <Text style={[styles.chipText, active ? styles.chipTextActive : { color: meta.color }]}>
                        {meta.sublabel}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            <View style={styles.divider} />

            <Text style={styles.sectionHeader}>Buddha Steps</Text>
            {ARRAY_FIELDS.map(({ key, label }) => {
              const items: string[] = (editForm as Record<string, string[] | undefined>)[key] ?? [];
              return (
                <View key={key} style={styles.fieldGroup}>
                  <Text style={styles.fieldLabel}>{label}</Text>
                  {items.map((item, i) => (
                    <View key={i} style={styles.arrayRow}>
                      <TextInput
                        style={[styles.textInput, { flex: 1 }]}
                        value={item}
                        onChangeText={(v) => updateEditArrayItem(key, i, v)}
                      />
                      <Pressable onPress={() => removeEditArrayItem(key, i)} style={styles.removeButton}>
                        <Text style={styles.dangerText}>✕</Text>
                      </Pressable>
                    </View>
                  ))}
                  <Pressable style={styles.smallOutlineButton} onPress={() => addEditArrayItem(key)}>
                    <Text style={styles.smallOutlineButtonText}>+ Add</Text>
                  </Pressable>
                </View>
              );
            })}

            <View style={styles.divider} />

            <Text style={styles.sectionHeader}>Full Profile</Text>
            {TEXT_FIELDS.map(({ key, label }) => (
              <View key={key} style={styles.fieldGroup}>
                <Text style={styles.fieldLabel}>{label}</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea]}
                  value={(editForm[key] as string | undefined) ?? ''}
                  onChangeText={(v) => updateEditField(key, v)}
                  multiline
                  numberOfLines={3}
                />
              </View>
            ))}

            <View style={styles.divider} />

            <Text style={styles.sectionHeader}>Spectrum</Text>
            <View style={styles.spectrumEditRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.fieldLabel}>Negative</Text>
                <TextInput
                  style={styles.textInput}
                  value={editForm.spectrum?.negative ?? ''}
                  onChangeText={(v) =>
                    updateEditField('spectrum', {
                      ...(editForm.spectrum ?? { negative: '', neutral: '', positive: '' }),
                      negative: v,
                    })
                  }
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.fieldLabel}>Neutral</Text>
                <TextInput
                  style={styles.textInput}
                  value={editForm.spectrum?.neutral ?? ''}
                  onChangeText={(v) =>
                    updateEditField('spectrum', {
                      ...(editForm.spectrum ?? { negative: '', neutral: '', positive: '' }),
                      neutral: v,
                    })
                  }
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.fieldLabel}>Positive</Text>
                <TextInput
                  style={styles.textInput}
                  value={editForm.spectrum?.positive ?? ''}
                  onChangeText={(v) =>
                    updateEditField('spectrum', {
                      ...(editForm.spectrum ?? { negative: '', neutral: '', positive: '' }),
                      positive: v,
                    })
                  }
                />
              </View>
            </View>

            <View style={styles.divider} />

            <Text style={styles.sectionHeader}>Deity Pair</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{ flex: 1, gap: 6 }}>
                <Text style={styles.fieldLabel}>Negative Deity Name</Text>
                <TextInput
                  style={styles.textInput}
                  value={editForm.deity_pair?.negative?.name ?? ''}
                  onChangeText={(v) =>
                    updateEditField('deity_pair', {
                      ...(editForm.deity_pair ?? {
                        negative: { name: '', icon: '' },
                        positive: { name: '', icon: '' },
                      }),
                      negative: { ...(editForm.deity_pair?.negative ?? { name: '', icon: '' }), name: v },
                    })
                  }
                />
                <Text style={styles.fieldLabel}>Negative Deity Icon</Text>
                <TextInput
                  style={styles.textInput}
                  value={editForm.deity_pair?.negative?.icon ?? ''}
                  onChangeText={(v) =>
                    updateEditField('deity_pair', {
                      ...(editForm.deity_pair ?? {
                        negative: { name: '', icon: '' },
                        positive: { name: '', icon: '' },
                      }),
                      negative: { ...(editForm.deity_pair?.negative ?? { name: '', icon: '' }), icon: v },
                    })
                  }
                />
              </View>
              <View style={{ flex: 1, gap: 6 }}>
                <Text style={styles.fieldLabel}>Positive Deity Name</Text>
                <TextInput
                  style={styles.textInput}
                  value={editForm.deity_pair?.positive?.name ?? ''}
                  onChangeText={(v) =>
                    updateEditField('deity_pair', {
                      ...(editForm.deity_pair ?? {
                        negative: { name: '', icon: '' },
                        positive: { name: '', icon: '' },
                      }),
                      positive: { ...(editForm.deity_pair?.positive ?? { name: '', icon: '' }), name: v },
                    })
                  }
                />
                <Text style={styles.fieldLabel}>Positive Deity Icon</Text>
                <TextInput
                  style={styles.textInput}
                  value={editForm.deity_pair?.positive?.icon ?? ''}
                  onChangeText={(v) =>
                    updateEditField('deity_pair', {
                      ...(editForm.deity_pair ?? {
                        negative: { name: '', icon: '' },
                        positive: { name: '', icon: '' },
                      }),
                      positive: { ...(editForm.deity_pair?.positive ?? { name: '', icon: '' }), icon: v },
                    })
                  }
                />
              </View>
            </View>

            <View style={styles.divider} />

            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Spectrum Rating: {editForm.spectrum_rating ?? 5}/10</Text>
              <RatingRow
                value={editForm.spectrum_rating ?? 5}
                onChange={(n) => updateEditField('spectrum_rating', n)}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Check-in Reminder</Text>
              <TextInput
                style={styles.textInput}
                placeholder="e.g. daily, weekly, etc."
                placeholderTextColor={c.slate}
                value={editForm.check_in_reminder ?? ''}
                onChangeText={(v) => updateEditField('check_in_reminder', v)}
              />
            </View>

            <View style={styles.fieldGroup}>
              <Text style={styles.fieldLabel}>Status</Text>
              <View style={styles.chipRow}>
                {(['active', 'resolved'] as HabitStatus[]).map((s) => {
                  const active = (editForm.status ?? 'active') === s;
                  return (
                    <Pressable
                      key={s}
                      style={[styles.chip, active && styles.chipActiveNeutral]}
                      onPress={() => updateEditField('status', s)}
                    >
                      <Text style={[styles.chipText, active && styles.chipTextActive]}>
                        {s === 'active' ? 'Active' : 'Resolved'}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            <View style={styles.divider} />

            <Pressable
              style={[styles.outlineButton, styles.dangerOutlineButton, { marginBottom: 24 }]}
              onPress={() => {
                if (editForm.id) {
                  const habit = habits.find((h) => h.id === editForm.id);
                  if (habit) confirmDelete(habit);
                }
              }}
            >
              <Text style={[styles.outlineButtonText, styles.dangerText]}>Delete Habit</Text>
            </Pressable>
          </ScrollView>
        </View>
      </Modal>

      {/* -------------------------------------------------------------- */}
      {/* Per-habit coaching chat (moyri_chat_messages via /api/ai/moyri) */}
      {/* -------------------------------------------------------------- */}
      <Modal
        visible={Boolean(chatHabitId)}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={closeChat}
      >
        {(() => {
          const chatHabit = habits.find((h) => h.id === chatHabitId) ?? null;
          return (
            <KeyboardAvoidingView
              style={styles.modalRoot}
              behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            >
<PageBg />
              <View style={styles.modalHeaderBar}>
                <Pressable style={styles.backButton} onPress={closeChat}>
                  <Text style={styles.backButtonText}>Close</Text>
                </Pressable>
                <Text style={styles.modalHeaderTitle} numberOfLines={1}>
                  Chat{chatHabit ? `: ${chatHabit.name}` : ''}
                </Text>
                <View style={{ width: 56 }} />
              </View>
              <ScrollView
                ref={chatScrollRef}
                style={{ flex: 1 }}
                contentContainerStyle={styles.chatScrollContent}
                onContentSizeChange={() => chatScrollRef.current?.scrollToEnd({ animated: true })}
              >
                {chatLoading ? (
                  <ActivityIndicator color={c.blueText} />
                ) : chatMessages.length === 0 ? (
                  <Text style={styles.chatPlaceholder}>
                    Ask questions about{chatHabit ? ` ${chatHabit.name}` : ' this habit'}, get advice, or work
                    through challenges with AI guidance.
                  </Text>
                ) : (
                  chatMessages.map((msg) => (
                    <View
                      key={msg.id}
                      style={[
                        styles.chatBubble,
                        msg.role === 'user' ? styles.chatBubbleUser : styles.chatBubbleAssistant,
                      ]}
                    >
                      <Text style={styles.chatBubbleText}>{msg.content}</Text>
                    </View>
                  ))
                )}
                {chatSending && (
                  <View style={[styles.chatBubble, styles.chatBubbleAssistant]}>
                    <ActivityIndicator size="small" color={c.slate} />
                  </View>
                )}
              </ScrollView>
              <View style={styles.chatInputRow}>
                <TextInput
                  style={styles.chatInput}
                  placeholder={chatHabit ? `Message about ${chatHabit.name}...` : 'Message...'}
                  placeholderTextColor={c.slate}
                  value={chatInput}
                  onChangeText={setChatInput}
                  onSubmitEditing={() => void sendChatMessage()}
                  returnKeyType="send"
                  multiline
                />
                <Pressable
                  style={styles.chatSendButton}
                  onPress={() => void sendChatMessage()}
                  disabled={!chatInput.trim() || chatSending}
                >
                  <Text style={styles.chatSendText}>Send</Text>
                </Pressable>
              </View>
            </KeyboardAvoidingView>
          );
        })()}
      </Modal>

      {/* -------------------------------------------------------------- */}
      {/* AI Habit Builder chat (creates habit row on completion)         */}
      {/* -------------------------------------------------------------- */}
      <Modal
        visible={builderOpen}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={closeBuilder}
      >
        <KeyboardAvoidingView
          style={styles.modalRoot}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
<PageBg />
          <View style={styles.modalHeaderBar}>
            <Pressable style={styles.backButton} onPress={closeBuilder}>
              <Text style={styles.backButtonText}>Close</Text>
            </Pressable>
            <Text style={styles.modalHeaderTitle} numberOfLines={1}>
              AI Habit Builder
              {builderCategory ? ` (${CATEGORY_META[builderCategory].sublabel})` : ''}
            </Text>
            {builderNewHabitId ? (
              <Pressable style={styles.backButton} onPress={viewBuiltHabit}>
                <Text style={[styles.backButtonText, { fontWeight: '900' }]}>View</Text>
              </Pressable>
            ) : (
              <View style={{ width: 56 }} />
            )}
          </View>
          <ScrollView
            ref={builderScrollRef}
            style={{ flex: 1 }}
            contentContainerStyle={styles.chatScrollContent}
            onContentSizeChange={() => builderScrollRef.current?.scrollToEnd({ animated: true })}
          >
            {builderMessages.map((msg, i) => (
              <View
                key={i}
                style={[
                  styles.chatBubble,
                  msg.role === 'user' ? styles.chatBubbleUser : styles.chatBubbleAssistant,
                ]}
              >
                <Text style={styles.chatBubbleText}>{msg.content}</Text>
              </View>
            ))}
            {builderSending && (
              <View style={[styles.chatBubble, styles.chatBubbleAssistant]}>
                <ActivityIndicator size="small" color={c.slate} />
              </View>
            )}
          </ScrollView>
          <View style={styles.chatInputRow}>
            <TextInput
              style={styles.chatInput}
              placeholder="Describe the habit you want to build..."
              placeholderTextColor={c.slate}
              value={builderInput}
              onChangeText={setBuilderInput}
              onSubmitEditing={() => void sendBuilderMessage()}
              returnKeyType="send"
              multiline
            />
            <Pressable
              style={styles.chatSendButton}
              onPress={() => void sendBuilderMessage()}
              disabled={!builderInput.trim() || builderSending}
            >
              <Text style={styles.chatSendText}>Send</Text>
            </Pressable>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  scrollContent: { padding: 12, gap: 12, paddingBottom: 32 },
  error: { color: c.danger, fontSize: 13, paddingHorizontal: 12, paddingTop: 8 },
  empty: { color: c.slate, fontStyle: 'italic', textAlign: 'center', marginVertical: 12 },
  mutedText: { color: t.ivorySoft, fontSize: 13, fontStyle: 'italic' },

  header: { marginBottom: 4 },
  title: { fontSize: 24, fontWeight: '900', color: c.blueText, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  subtitle: { fontSize: 13, color: c.slate, marginTop: 2 },

  categorySection: { gap: 8 },
  categoryHeaderCard: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    paddingVertical: 12,
    ...neu.raised,
  },
  categoryHeaderTitle: { fontSize: 17, fontWeight: '800', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  categoryHeaderCount: { fontSize: 12, color: c.slate, marginTop: 4 },

  previewGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  previewCard: {
    width: '47%',
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    gap: 6,
    alignItems: 'flex-start',
    ...neu.raisedSm,
  },
  previewName: { fontSize: 13, fontWeight: '700', color: c.text, width: '100%' },
  countRow: { flexDirection: 'row', gap: 12 },
  countText: { fontSize: 12, fontWeight: '700' },

  addButton: {
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    backgroundColor: t.glass,
    ...neu.raised,
  },
  addButtonText: { color: c.blueText, fontWeight: '800', fontSize: 13 },
  addButtonRow: { flexDirection: 'row', gap: 8 },
  addButtonAccent: { backgroundColor: c.lime, ...neu.glowPrimary },

  categoryScreenHeader: { gap: 4, marginBottom: 4 },
  categoryScreenTitle: { fontSize: 20, fontWeight: '900', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  backButton: { paddingVertical: 6, paddingHorizontal: 4 },
  backButtonText: { color: c.blueText, fontWeight: '700', fontSize: 15 },

  listCard: {
    flexDirection: 'row',
    gap: 12,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 12,
    ...neu.raised,
  },
  listCardBody: { flex: 1, gap: 4 },
  listCardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  listCardName: { fontSize: 15, fontWeight: '800', color: c.text, flexShrink: 1 },
  listCardSummary: { fontSize: 12, color: c.slate },

  badge: {
    backgroundColor: t.glass,
    borderRadius: radii.pill,
    paddingHorizontal: 8,
    paddingVertical: 3,
    ...neu.raisedSm,
  },
  badgeText: { fontSize: 11, fontWeight: '700', color: c.slate },

  tileFallback: { alignItems: 'center', justifyContent: 'center' },
  tileFallbackText: { color: t.onGold, fontWeight: '900' },

  modalRoot: { flex: 1, backgroundColor: t.night },
  modalHeaderBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: t.glass,
    ...neu.raised,
  },
  modalHeaderTitle: { flex: 1, textAlign: 'center', fontSize: 15, fontWeight: '800', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },

  heroWrap: { alignItems: 'center', gap: 8, marginBottom: 4 },
  heroName: { fontSize: 20, fontWeight: '900', color: c.text },
  heroBadgeRow: { flexDirection: 'row', gap: 8 },
  summaryText: { fontSize: 13, color: c.slate, fontStyle: 'italic', textAlign: 'center', paddingHorizontal: 8 },

  card: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 12,
    gap: 8,
    ...neu.raised,
  },
  cardHeader: { fontSize: 13, fontWeight: '800' },

  bulletRow: { flexDirection: 'row', gap: 6 },
  bulletDash: { fontWeight: '800' },
  bulletText: { fontSize: 13, color: c.text, flex: 1 },

  spectrumRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 10,
    paddingVertical: 10,
    gap: 6,
    ...neu.raised,
  },
  spectrumText: { fontSize: 11, fontWeight: '700', flex: 1, textAlign: 'center' },
  spectrumTextMuted: { fontSize: 11, color: c.slate, flex: 1, textAlign: 'center' },

  logButtonRow: { flexDirection: 'row', gap: 8 },
  logButton: { flex: 1, borderRadius: radii.tile, paddingVertical: 12, alignItems: 'center' },
  logButtonText: { color: t.onGold, fontWeight: '800', fontSize: 13 },

  statsRow: { flexDirection: 'row', gap: 8 },
  statBox: {
    flex: 1,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 12,
    alignItems: 'center',
    ...neu.raised,
  },
  statNumber: { fontSize: 22, fontWeight: '900' },
  statLabel: { fontSize: 11, color: c.slate, marginTop: 2 },

  section: { gap: 6 },
  sectionHeader: { fontSize: 14, fontWeight: '800', color: c.text },
  sectionBody: { fontSize: 13, color: c.slate, lineHeight: 19 },

  ratingRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  ratingDot: {
    padding: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    ...neu.raised,
  },
  ratingDotActive: { backgroundColor: c.lime, ...neu.inset },
  ratingDotText: { fontSize: 12, fontWeight: '700', color: c.slate },
  ratingDotTextActive: { color: c.blueText },
  ratingLabelsRow: { flexDirection: 'row', justifyContent: 'space-between' },
  ratingLabelText: { fontSize: 11, color: c.slate },

  linkedRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  linkedBadge: {
    backgroundColor: t.glass,
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 5,
    ...neu.raisedSm,
  },
  linkedBadgeText: { fontSize: 12, color: c.blueText, fontWeight: '700' },

  logRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  logDate: { fontSize: 13, color: c.slate },
  logNote: { fontSize: 12, color: c.text },

  footerButtonRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
  outlineButton: {
    flex: 1,
    borderRadius: radii.tile,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: t.glass,
    ...neu.raised,
  },
  outlineButtonText: { color: c.blueText, fontWeight: '800', fontSize: 13 },
  dangerOutlineButton: {},
  dangerText: { color: c.danger },

  fieldGroup: { gap: 6 },
  fieldLabel: { fontSize: 12, fontWeight: '700', color: c.slate },
  textInput: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 14,
    color: c.text,
    ...neu.insetDeep,
  },
  textArea: { minHeight: 72, textAlignVertical: 'top' },

  chipRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  chip: {
    backgroundColor: t.glass,
    borderRadius: radii.pill,
    paddingHorizontal: 14,
    paddingVertical: 8,
    ...neu.raisedSm,
  },
  chipActiveNeutral: { backgroundColor: c.lime, ...neu.inset },
  chipText: { fontSize: 13, fontWeight: '700', color: c.slate },
  chipTextActive: { color: t.onGold },

  divider: { height: 1, backgroundColor: c.glassRim, marginVertical: 4 },

  arrayRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  removeButton: { padding: 8 },
  smallOutlineButton: {
    alignSelf: 'flex-start',
    backgroundColor: t.glass,
    borderRadius: radii.pill,
    paddingHorizontal: 12,
    paddingVertical: 6,
    ...neu.raisedSm,
  },
  smallOutlineButtonText: { fontSize: 12, fontWeight: '700', color: c.blueText },

  spectrumEditRow: { flexDirection: 'row', gap: 8 },
  linkText: { fontSize: 13, fontWeight: '700', color: c.blueText },

  chatScrollContent: { padding: 14, gap: 8, flexGrow: 1 },
  chatPlaceholder: { color: c.slate, fontStyle: 'italic', textAlign: 'center', marginTop: 24, fontSize: 13 },
  chatBubble: {
    maxWidth: '85%',
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  chatBubbleUser: { alignSelf: 'flex-end', backgroundColor: c.lime, ...neu.raisedSm },
  chatBubbleAssistant: { alignSelf: 'flex-start', backgroundColor: t.glass, ...neu.raised },
  chatBubbleText: { fontSize: 14, color: c.text },
  chatInputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    padding: 12,
    backgroundColor: t.glass,
    ...neu.raised,
  },
  chatInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: c.text,
    maxHeight: 100,
    ...neu.insetDeep,
  },
  chatSendButton: {
    backgroundColor: c.lime,
    borderRadius: radii.tile,
    paddingHorizontal: 16,
    paddingVertical: 12,
    justifyContent: 'center',
    ...neu.raisedSm,
  },
  chatSendText: { fontWeight: '900', color: c.blueText, fontSize: 13 },
}),
);
